import { Injectable } from '@angular/core';
import { LegacyPageEvent as PageEvent } from '@angular/material/legacy-paginator';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import {
  BehaviorSubject,
  Observable,
  Subscription,
  throwError,
  Subject,
} from 'rxjs';

//Config
import { ILocation } from '../../../../location/interfaces/location.interface';
import { IStock, IStockStatus, IStockGroup } from '../../../../stock/interfaces';
import {
  EAgGridFilter,
  IAgGridFilter,
} from '../../../interfaces/ag-grid-filter.interface';
import { IWarehouseGroup } from '../../../interfaces/location-warehouse.interface';
import {
  IPagination,
  DEFAULT_PAGINATION,
} from '../../../interfaces/paginator.interface';
import { AgGridFilterApiService } from '../../api/ag-grid-filter-api.service';
import { LocationWarehouseGroupApiService } from '../../api/location/location-warehouse-group-api.service';
import { StockApiService } from '../../api/stock-api.service';

import { StockAgTableConfig } from './../../../../stock/configs/stock-ag-table.config';
import { SnackBarConfig } from './../../../configs/snackbar.config';

//Interfaces

import { LocalStorageKeys } from './../../../interfaces/localstorage.interface';

//Services

import { InventoryApiService } from './../../api/inventory-api.service';
import { RfqApiService } from './../../api/rfq/rfq-api.service';

@Injectable({
  providedIn: 'root',
})
export class StockFacadeService {
  fields = StockAgTableConfig.fields;

  // STOCKS
  private _stocks$ = new BehaviorSubject<IStock[]>(<IStock[]>[]);
  private _stocksMeta$ = new BehaviorSubject<IPagination>(DEFAULT_PAGINATION);
  private _loadingStocks$ = new BehaviorSubject<boolean>(false);

  // WAREHOUSES
  private _warehouseGroups$ = new BehaviorSubject<IWarehouseGroup[]>(
    <IWarehouseGroup[]>[],
  );
  private _warehouseGroupsMeta$ = new BehaviorSubject<IPagination>(<IPagination>{});
  private _loadingWarehouseGroups$ = new BehaviorSubject<boolean>(false);
  private _locationWarehouses$ = new BehaviorSubject<ILocation[]>(<ILocation[]>[]);

  // STOCK STATUS
  private _currentStockStatus$ = new BehaviorSubject<string | null>(null);
  private _stockStatuses$ = new BehaviorSubject<IStockStatus[]>(<IStockStatus[]>[]);
  private _loadingStockStatuses$ = new BehaviorSubject<boolean>(false);

  // CUSTOM FILTER
  private _currentCustomFilter$ = new BehaviorSubject<any | null>(null);
  private _customFilters$ = new BehaviorSubject<IAgGridFilter[]>(
    <IAgGridFilter[]>[],
  );
  private _loadingCustomFilters$ = new BehaviorSubject<boolean>(false);

  // GROUPS
  private _groups$ = new BehaviorSubject<IStockGroup[]>(<IStockGroup[]>[]);
  private _groupBy$ = new BehaviorSubject<string>('');

  //SELECTED ROW
  private _selectedRows$ = new BehaviorSubject<IStock[]>(<IStock[]>[]);

  private _currentQueryParams = new BehaviorSubject<any>(<any>{
    ...StockAgTableConfig.defaultQueryParams,
  });

  private _columnFilters = new BehaviorSubject<any>({});
  columnFilters$ = this._columnFilters.asObservable();

  canExportUnitCost :boolean = false;

  private agGridDataChangeSource = new Subject<void>();
  agGridDataChanged$ = this.agGridDataChangeSource.asObservable();

  private agGridResetDataSource = new Subject<void>();
  agGridResetData$ = this.agGridResetDataSource.asObservable();

  constructor(
    private stockService :StockApiService,
    private warehouseGroupService :LocationWarehouseGroupApiService,
    private agGridFilterApiService :AgGridFilterApiService,
    private snackBar :MatSnackBar,
    private inventoryApiService :InventoryApiService,
    private rfqApiService :RfqApiService,
  ) {}

  // SERVICE API CALLS
  getStocks(params :{ [param :string] :string | string[] }) :Subscription {
    this._loadingStocks$.next(true);

    return this.stockService.get(params).subscribe({
      next: (response :any) => {
        const { data, meta } = response;
        this._stocks$.next(data);
        this._stocksMeta$.next(meta);

        this.notifyAgGridDataChange();

        if (params['warehouseGroupIds']) {
          const warehouseGroupIds = (params['warehouseGroupIds'] as string).split(
            ',',
          );
          const warehouses = this._locationWarehouses$
            .getValue()
            .filter((warehouse :any) =>
              warehouseGroupIds.includes(warehouse.warehouseGroupId.toString()),
            );
          this._locationWarehouses$.next(warehouses);
        }
      },
      error: (response) => {
        this.snackBar.open(
          response?.error?.message || 'Error fetching stocks',
          'Fail',
        );

        return throwError(() => new Error('Error fetching stocks'));
      },
      complete: () => {
        this._loadingStocks$.next(false);
      },
    });
  }

  getWarehouseGroups(params :{ [param :string] :string | string[] }) :Subscription {
    this._loadingWarehouseGroups$.next(true);

    return this.warehouseGroupService.getV2(params).subscribe({
      next: (response :any) => {
        const { data, meta } = response;
        this._warehouseGroups$.next(data);
        this._warehouseGroupsMeta$.next(meta);

        const warehouses = data
          .map((warehouseGroup :any) => {
            const warehousesWithGroupId = warehouseGroup.warehouses.map(
              (warehouse :any) => {
                return {
                  ...warehouse,
                  warehouseGroupId: warehouseGroup.id,
                };
              },
            );

            return warehousesWithGroupId;
          })
          .flat()
          .sort(
            (a :ILocation, b :ILocation) =>
              new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
          );
        this._locationWarehouses$.next(warehouses);
      },
      error: (error) => {
        this.snackBar.open(
          error?.error?.message || 'Error fetching warehouse groups',
          'Fail',
        );

        return throwError(() => new Error('Error fetching warehouse groups'));
      },
      complete: () => {
        this._loadingWarehouseGroups$.next(false);
      },
    });
  }

  getGroups() :Subscription {
    this._loadingStocks$.next(true);

    const { defaultGroupQueryParams } = StockAgTableConfig;

    const limit = Number(defaultGroupQueryParams.limit) || 100;

    const page = this.groups.length
      ? Math.ceil(this.groups.length / limit) + 1
      : defaultGroupQueryParams.page;

    if (this.groupBy) {
      this.currentQueryParams.groupBy = this.groupBy;
    }

    const groupParams = {
      ...this.currentQueryParams,
      limit: limit,
      page: page,
    };

    return this.stockService.getGroups(groupParams).subscribe({
      next: (response :any) => {
        const { data, meta } = response;

        if (this.groups.length) {
          this._groups$.next([...this.groups, ...data]);
        } else {
          this._groups$.next(data);
        }

        if (
          page < meta.last_page &&
          this.currentGroupsTotalCount < this.expectedRecordsCount
        ) {
          this.getGroups();
        } else {
          this._loadingStocks$.next(false);
          this.getStocks(this.currentQueryParams);
        }
      },
      error: (response) => {
        this.snackBar.open(
          response?.error?.message || 'Error fetching groups',
          'Fail',
        );

        return throwError(() => new Error('Error fetching groups'));
      },
    });
  }

  getStockStatuses() :Subscription {
    this._loadingStockStatuses$.next(true);

    return this.stockService.getStatuses().subscribe({
      next: (response :IStockStatus[]) => {
        this._stockStatuses$.next(response);
      },
      error: (response) => {
        this.snackBar.open(
          response?.error?.message || 'Error fetching stocks',
          'Fail',
        );

        return throwError(() => new Error('Error fetching stock statuses'));
      },
      complete: () => {
        this._loadingStockStatuses$.next(false);
      },
    });
  }

  getStockCustomFilters(params ?:{
    [param :string] :string | string[];
  }) :Subscription {
    this._loadingCustomFilters$.next(true);

    return this.agGridFilterApiService.get(params).subscribe({
      next: (response :any) => {
        this._customFilters$.next(response.data);
      },
      error: (error) => {
        this.snackBar.open(
          error?.error?.message || 'Error fetching custom filters',
          'Fail',
        );

        return throwError(() => new Error('Error fetching custom filters'));
      },
      complete: () => {
        this._loadingCustomFilters$.next(false);
      },
    });
  }

  createCustomFilter(data :{
    name :string;
    filters :any;
    module :EAgGridFilter;
  }) :Subscription {
    return this.agGridFilterApiService.create(data).subscribe({
      next: (response :any) => {
        this._customFilters$.next([...this.customFilters, response]);
        this.snackBar.open('Custom filter created successfully', 'Success', {
          duration: SnackBarConfig.DEFAULT.duration,
        });
      },
      error: (error) => {
        this.snackBar.open(
          error?.error?.message || 'Error creating custom filter',
          'Fail',
        );

        return throwError(() => new Error('Error creating custom filter'));
      },
    });
  }

  deleteCustomFilter(id :number) :Subscription {
    return this.agGridFilterApiService.delete(id).subscribe({
      next: (response :any) => {
        this._customFilters$.next(
          this.customFilters.filter((filter) => filter.id !== id),
        );
        this.snackBar.open('Custom filter deleted successfully', 'Success', {
          duration: SnackBarConfig.DEFAULT.duration,
        });
      },
      error: (error) => {
        this.snackBar.open(
          error?.error?.message || 'Error deleting custom filter',
          'Fail',
        );

        return throwError(() => new Error('Error deleting custom filter'));
      },
    });
  }

  updateCustomFilter(id :number, data :IAgGridFilter) :Subscription {
    return this.agGridFilterApiService.update(id, data).subscribe({
      next: (response :any) => {
        this._customFilters$.next([
          ...this.customFilters.filter((filter) => filter.id !== id),
          response,
        ]);
        this.snackBar.open('Custom filter updated successfully', 'Success', {
          duration: SnackBarConfig.DEFAULT.duration,
        });
      },
      error: (error) => {
        this.snackBar.open(
          error?.error?.message || 'Error updating custom filter',
          'Fail',
        );

        return throwError(() => new Error('Error updating custom filter'));
      },
    });
  }

  inventoryRecountPending() :Observable<any> {
    return this.inventoryApiService.recountPending();
  }
  inventoryExport(params :{ [param :string] :string | string[] }) :Observable<any> {
    return this.inventoryApiService.exportV2(params);
  }

  getRfqs(params :{ [param :string] :string | string[] }) :Observable<any> {
    return this.rfqApiService.get(params);
  }
  rfqResetBaselineToZero(ids :number[]) :Observable<any> {
    return this.rfqApiService.resetBaselineToZero(ids);
  }

  rfqResetBaselineToZeroV2(ids :number[]) :Observable<any> {
    return this.rfqApiService.resetBaselineToZero(ids);
  }
  // END SERVICE API CALLS

  notifyAgGridDataChange() :void {
    this.agGridDataChangeSource.next();
  }

  notifyAgGridResetData() :void {
    this.agGridResetDataSource.next();
  }

  pushSelectedRow(row :IStock) :void {
    this._selectedRows$.next([...this.selectedRows, row]);
  }

  spliceSelectedRow(index :number) :void {
    const currentSelectedRows = this._selectedRows$.getValue();

    if (index > -1 && index < currentSelectedRows.length) {
      currentSelectedRows.splice(index, 1);
      this._selectedRows$.next(currentSelectedRows);
    }
  }

  filterChanged(
    data :{
      filterModel ?:any;
      columnState ?:any;
    },
    options ?:{
      disableReload ?:boolean;
    },
  ) :void {
    options = {
      disableReload: false,
      ...options,
    };

    const { filterModel = {}, columnState = {} } = data;
    const currentColumnState = data.columnState;
    const sortedColumn = currentColumnState?.find((col :any) => col.sort !== null);
    const params = sortedColumn
      ? {
          sortField: sortedColumn.colId,
          sortDirection: sortedColumn.sort,
        }
      : {};

    this._columnFilters.next({ filterModel, columnState });

    this.updatePageStateAndQueryParams({
      filterModel,
      columnState,
      params,
      callStocksApi: !options.disableReload,
    });
  }

  sortChanged(data :{ filterModel ?:any; columnState ?:any }) :void {
    const { filterModel = {}, columnState = {} } = data;

    const currentColumnState = data.columnState;
    const sortedColumn = currentColumnState.find((col :any) => col.sort !== null);
    const params = sortedColumn
      ? {
          sortField: sortedColumn.colId,
          sortDirection: sortedColumn.sort,
        }
      : {};

    this.updatePageStateAndQueryParams({
      filterModel: data.filterModel,
      columnState: currentColumnState,
      params,
    });
  }

  paginationChange(pagination :PageEvent) :void {
    const params = {
      ...this.currentQueryParams,
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
    };

    this.updatePageStateAndQueryParams({
      filterModel: this.storedPageState.filterModel,
      columnState: this.storedPageState.columnState,
      params,
    });
  }

  columnVisible(data :{ filterModel ?:any; columnState ?:any }) :void {
    const { filterModel = {}, columnState = {} } = data;

    const pageState = {
      filterModel: filterModel,
      columnState: columnState,
    };

    localStorage.setItem(
      LocalStorageKeys.STOCK_AG_STATE,
      JSON.stringify(pageState),
    );
  }

  gotoPage(page :string) :void {
    if (page === '') return;

    const pageValueInput = Number(page);
    const pageEvent = new PageEvent();
    pageEvent.pageIndex = pageValueInput - 1;
    pageEvent.pageSize = this.stocksMeta.per_page;
    pageEvent.length = this.stocksMeta.total;

    this.paginationChange(pageEvent);
  }

  resetFilters() :void {
    this._currentQueryParams.next({ ...StockAgTableConfig.defaultQueryParams });
    this._columnFilters.next({ filterModel: null, columnState: null });
    localStorage.removeItem(LocalStorageKeys.STOCK_AG_STATE);
  }

  updateGroupingAndState(
    value :string,
    currentFilterModel :any,
    currentColumnState :any,
  ) :void {
    this.setGroupState(value);
    this.resetGroupings();
    if (value) {
      this.getGroups();
    } else {
      this.filterChanged({
        filterModel: currentFilterModel,
        columnState: currentColumnState,
      });
    }
  }

  resetGroupings() :void {
    this._groups$.next([]);
    this._stocksMeta$.next({
      ...this.stocksMeta,
      current_page: 1,
    });
  }

  resetSelectedRows() :void {
    this._selectedRows$.next([]);
    this.notifyAgGridResetData();
  }

  setGroupState(groupState :string) :void {
    this._groupBy$.next(groupState);
    localStorage.setItem(LocalStorageKeys.STOCK_AG_GROUP_STATE, groupState);
  }

  setCurrentStockStatus(status :string | null) :void {
    const newFilterModelForStockStatus = {
      stockStatus: {
        filter: status,
        filterType: 'text',
        type: 'contains',
      },
    };

    const currentFilterModel = this.storedPageState.filterModel || {};

    const updatedFilterModel = {
      ...currentFilterModel,
      ...newFilterModelForStockStatus,
    };

    this._currentCustomFilter$.next(null);
    this._currentStockStatus$.next(status);

    this.updatePageStateAndQueryParams({
      filterModel: updatedFilterModel,
      columnState: this.storedPageState.columnState,
      params: { stockStatus: status },
    });
  }

  setCurrentCustomFilter(data :IAgGridFilter) :void {
    const { filterModel = {}, columnState = {} } = data.filters;

    this._currentStockStatus$.next(null);
    this._currentCustomFilter$.next(data.filters);

    this.updatePageStateAndQueryParams({
      filterModel,
      columnState,
    });
  }

  setCanExportUnitCost(canExportUnitCost :boolean) :void {
    this.canExportUnitCost = canExportUnitCost;
  }

  setLocationWarehouses(warehouses :any[]) :void {
    this._locationWarehouses$.next(warehouses);
  }

  // PRIVATE METHODS
  private updatePageStateAndQueryParams(data :{
    filterModel ?:any;
    columnState ?:any;
    params ?:any;
    callStocksApi ?:boolean;
  }) :void {
    const {
      filterModel = {},
      columnState = {},
      params = {},
      callStocksApi = true,
    } = data;

    // This adjustment is necessary due to the Stock Status filter being disabled in ag-Grid.
    // It ensures that, when a specific stock status is selected, the filter model includes this status, using an "equals" condition to filter the data accordingly.
    if (
      this.currentCustomFilter &&
      this.currentCustomFilter.filterModel &&
      this.currentCustomFilter.filterModel.stockStatus
    ) {
      filterModel[this.fields.STOCK_STATUS] =
        this.currentCustomFilter.filterModel.stockStatus;
    } else if (this.currentStockStatus) {
      filterModel[this.fields.STOCK_STATUS] = {
        type: 'equals',
        filter: this.currentStockStatus,
      };
    }

    if (this.groupBy) {
      params.groupBy = this.groupBy;
    }

    const pageState = { filterModel, columnState };

    localStorage.setItem(
      LocalStorageKeys.STOCK_AG_STATE,
      JSON.stringify(pageState),
    );

    const filters = this.buildQueryParams(filterModel, params);
    this._currentQueryParams.next({ ...filters });

    if (callStocksApi) {
      if (this.groupBy) {
        this._groups$.next([]);
        this.getGroups();
      } else {
        this.getStocks(this.currentQueryParams);
      }
    }
  }

  private buildQueryParams(
    filterModel :any,
    params :any = {},
  ) :{ [param :string] :string | string[] } {
    const queryParams = { ...StockAgTableConfig.defaultQueryParams, ...params };
    Object.keys(filterModel).forEach((key) => {
      const filter = filterModel[key];
      switch (key) {
        case this.fields.WAREHOUSE_GROUP:
          queryParams['warehouseGroupIds'] = filterModel[key].values.join(',');
          break;

        case this.fields.PRODUCT_TYPE:
          queryParams[key] = filter.values.join(',');
          break;

        case this.fields.INCOMING_QTY:
        case this.fields.ONHAND_QTY:
        case this.fields.PENDING_QTY:
        case this.fields.AVAILABLE_QTY:
          queryParams[key] = JSON.stringify(filter);
          break;

        default:
          queryParams[key] = filter.filter?.toString();
          break;
      }
    });

    if (queryParams.groupBy === '') {
      delete queryParams.groupBy;
    }

    return queryParams;
  }
  // END PRIVATE METHODS

  // STOCKS
  get stocks() :IStock[] {
    return this._stocks$.getValue();
  }

  get stocks$() :Observable<IStock[]> {
    return this._stocks$.asObservable();
  }

  get stocksMeta() :IPagination {
    return this._stocksMeta$.getValue();
  }

  get stocksMeta$() :Observable<IPagination> {
    return this._stocksMeta$.asObservable();
  }

  get loadingStocks() :boolean {
    return this._loadingStocks$.getValue();
  }

  get loadingStocks$() :Observable<boolean> {
    return this._loadingStocks$.asObservable();
  }
  // END STOCKS

  // STOCK STATUSES
  get currentStockStatus() :string | null {
    return this._currentStockStatus$.getValue();
  }

  get currentStockStatus$() :Observable<string | null> {
    return this._currentStockStatus$.asObservable();
  }

  get stockStatuses() :IStockStatus[] {
    return this._stockStatuses$.getValue();
  }

  get stockStatuses$() :Observable<IStockStatus[]> {
    return this._stockStatuses$.asObservable();
  }

  get loadingStockStatuses() :boolean {
    return this._loadingStockStatuses$.getValue();
  }

  get loadingStockStatuses$() :Observable<boolean> {
    return this._loadingStockStatuses$.asObservable();
  }
  // END STOCK STATUSES

  // CUSTOM FILTER
  get currentCustomFilter() :any | null {
    return this._currentCustomFilter$.getValue();
  }

  get currentCustomFilter$() :Observable<any | null> {
    return this._currentCustomFilter$.asObservable();
  }

  get customFilters() :IAgGridFilter[] {
    return this._customFilters$.getValue();
  }

  get customFilters$() :Observable<IAgGridFilter[]> {
    return this._customFilters$.asObservable();
  }

  get loadingCustomFilters() :boolean {
    return this._loadingCustomFilters$.getValue();
  }

  get loadingCustomFilters$() :Observable<boolean> {
    return this._loadingCustomFilters$.asObservable();
  }
  // END CUSTOM FILTER

  // WAREHOUSE

  get warehouseGroups() :IWarehouseGroup[] {
    return this._warehouseGroups$.getValue();
  }

  get warehouseGroups$() :Observable<IWarehouseGroup[]> {
    return this._warehouseGroups$.asObservable();
  }

  get warehouseGroupsMeta() :IPagination {
    return this._warehouseGroupsMeta$.getValue();
  }

  get warehouseGroupsMeta$() :Observable<IPagination> {
    return this._warehouseGroupsMeta$.asObservable();
  }

  get loadingWarehouseGroups() :boolean {
    return this._loadingWarehouseGroups$.getValue();
  }

  get loadingWarehouseGroups$() :Observable<boolean> {
    return this._loadingWarehouseGroups$.asObservable();
  }

  get locationWarehouses$() :Observable<any[]> {
    return this._locationWarehouses$.asObservable();
  }

  get locationWarehouses() :any[] {
    return this._locationWarehouses$.getValue();
  }
  // END WAREHOUSE GROUP

  // GROUPS
  get groups() :IStockGroup[] {
    return this._groups$.getValue();
  }

  get groups$() :Observable<IStockGroup[]> {
    return this._groups$.asObservable();
  }

  get groupBy() :string {
    return this._groupBy$.getValue();
  }

  get groupBy$() :Observable<string> {
    return this._groupBy$.asObservable();
  }

  get currentGroupsTotalCount() :number {
    return this.groups.reduce((acc, curr) => acc + curr.count, 0);
  }

  get expectedRecordsCount() :number {
    return (this.stocksMeta.current_page + 1) * this.stocksMeta.per_page;
  }
  // END GROUPS

  // SELECTED ROW
  get selectedRows() :IStock[] {
    return this._selectedRows$.getValue();
  }

  get selectedRows$() :Observable<IStock[]> {
    return this._selectedRows$.asObservable();
  }
  // END SELECTED ROW

  get currentQueryParams() :any {
    return this._currentQueryParams.getValue();
  }

  get storedPageState() :any {
    const storedData = localStorage.getItem(LocalStorageKeys.STOCK_AG_STATE);

    return storedData ? JSON.parse(storedData) : {};
  }
}
